import { useEffect, useMemo, useState } from "react";
import { Button, SearchBar, type Column } from "@legal-500/legal500-design-system";
import { editorialCategories } from "@/api/editorialCategories.service";
import type { LegacyPublication } from "@/api/legacy-publications.service";
import {
  refereesService,
  type Referee,
  type RefereeWithTranslations,
} from "@/api/referees.service";
import {
  submissionsService,
  type Submission,
  type TaxonomyMode,
} from "@/api/submissions.service.js";
import { DataFilters } from "@/components/DataFilters/DataFilters";
import { DataTable } from "@/components/DataTable/DataTable";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { PageHeader } from "@/components/PageHeader";
import { PaginationControls } from "@/components/PaginationControls";
import { TaxonomyToggle } from "@/components/TaxonomyToggle";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useFilters } from "@/hooks/useFilters";
import { usePublications } from "@/hooks/usePublications";
import { useReferees } from "@/hooks/useReferees";
import { useSubmissionUniques } from "@/hooks/useSubmissionUniques";
import { TAXONOMY_URL_PARAM, useTaxonomyMode } from "@/hooks/useTaxonomyMode";
import { useTranslations, type SubmissionWithTranslations } from "@/hooks/useTranslations.js";
import { useUrlParams } from "@/hooks/useUrlParams";
import { formatDateShort } from "@/utils/dateFormatter.js";
import {
  getSubmissionPracticeFullName,
  getSubmissionPracticeFullNameTranslated,
} from "@/utils/practiceName.js";
import {
  publicationOptionLabel,
  taxonomyFilterParams,
  taxonomyOptionFitsMode,
  taxonomyOptionValue,
  taxonomyValueFitsMode,
} from "@/utils/taxonomyFilter";
import styles from "../../CompanyUsers/CompanyUsers.module.css";
import refereeStyles from "./RefereeContactList.module.css";

const SEARCH_DEBOUNCE_MS = 300;

function RefereeContactList() {
  const { searchParams, updateUrl } = useUrlParams();
  const currentYear = new Date().getFullYear();
  const { toggleVisible, mode, taxonomy, ready } = useTaxonomyMode();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [filters, setFilters] = useState({
    publication: searchParams.get("publication") || "all",
    region: searchParams.get("region") || "all",
    edition: searchParams.get("edition") || String(currentYear),
    surveyAnswered: searchParams.get("surveyAnswered") || "all",
  });
  const [regionOptionsState, setRegionOptionsState] = useState<{ value: string; label: string }[]>([
    { value: "all", label: "Select publication first" },
  ]);
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(debouncedSearchTerm);
  // Resetting the page here, not in the change handler, keeps it to one list request per search.
  if (appliedSearchTerm !== debouncedSearchTerm) {
    setAppliedSearchTerm(debouncedSearchTerm);
    setCurrentPage(1);
  }
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const ITEMS_PER_PAGE = 50;

  const { activeFilters, handleFilterChange, handleClearFilter } = useFilters({
    filters,
    setFilters,
    updateUrl,
  });

  useEffect(() => {
    if (!ready) return;
    const stale = (["publication", "region"] as const).filter(
      key => !taxonomyValueFitsMode(filters[key], mode),
    );
    if (stale.length === 0) return;
    const cleared = Object.fromEntries(stale.map(key => [key, "all"]));
    setFilters(prev => ({ ...prev, ...cleared }));
    updateUrl(cleared);
  }, [ready, mode, filters.publication, filters.region]);

  const { publicationId, editionId } = taxonomyFilterParams(
    "publication",
    filters.publication,
    mode,
  );

  const { regionId, rankingEntityId } =
    filters.publication === "all" ? {} : taxonomyFilterParams("region", filters.region, mode);

  const { data: publicationsData } = usePublications(
    { edition: filters.edition },
    { enabled: mode === "legacy" },
  );

  const { data: publicationUniques } = useSubmissionUniques(
    "publication",
    { edition: filters.edition, taxonomy },
    { enabled: mode === "new" },
  );

  useEffect(() => {
    if (filters.publication === "all") {
      setRegionOptionsState([{ value: "all", label: "Select publication first" }]);
      return;
    }

    const loadRegions = async () => {
      try {
        if (mode === "new") {
          const uniques = await submissionsService.getUniques("region", { editionId, taxonomy });
          const options = uniques.values
            .filter(value => taxonomyOptionFitsMode("region", value, mode))
            .map(value => ({
              value: taxonomyOptionValue("region", value, mode),
              label: value.name,
            }));
          setRegionOptionsState([{ value: "all", label: "All regions" }, ...options]);
          return;
        }
        const resp = await editorialCategories.getAll({
          publicationId,
          type: "country",
          limit: 100,
        });
        const options =
          resp.editorialCategories?.map(cat => ({
            value: String(cat.id),
            label:
              (cat as unknown as { country?: string }).country ||
              (cat as unknown as { name?: string }).name ||
              (cat as unknown as { practiceArea?: string }).practiceArea ||
              (cat as unknown as { practiceGroup?: string }).practiceGroup ||
              `Region ${cat.id}`,
          })) ?? [];
        setRegionOptionsState([{ value: "all", label: "All regions" }, ...options]);
      } catch (_e) {
        setRegionOptionsState([{ value: "all", label: "Failed to load regions" }]);
      }
    };

    loadRegions();
  }, [filters.publication, publicationId, editionId, mode, taxonomy]);

  const { data, loading, error } = useReferees({
    limit: ITEMS_PER_PAGE,
    offset: (currentPage - 1) * ITEMS_PER_PAGE,
    publicationId,
    edition: filters.edition,
    regionId,
    editionId,
    rankingEntityId,
    taxonomy,
    search: appliedSearchTerm || undefined,
    surveyAnswered:
      filters.surveyAnswered === "all" ? undefined : (filters.surveyAnswered as "yes" | "no"),
  });

  const referees = useMemo(() => data?.referees || [], [data?.referees]);
  const pagination = data?.pagination || {
    total: 0,
    limit: ITEMS_PER_PAGE,
    offset: 0,
    hasMore: false,
  };

  const isAuthError =
    error &&
    ((error as Error & { status?: number }).status === 401 ||
      (error as Error & { status?: number }).status === 403);

  const {
    data: refereesWithTranslations,
    loading: translationsLoading,
    error: translationsError,
  } = useTranslations({
    entities: referees,
    enabled: !loading && !isAuthError && referees.length > 0,
    getEntityId: referee => referee.id,
  });

  const isLoading = loading || translationsLoading;
  const displayReferees = refereesWithTranslations.length > 0 ? refereesWithTranslations : referees;

  const columns: Column<RefereeWithTranslations>[] = [
    {
      header: "Firm",
      accessor: "firmName",
      render: (value: unknown) => (value === null || value === undefined ? "N/A" : String(value)),
    },
    {
      header: "Publication/Edition",
      accessor: "publicationName",
      render: (_value: unknown, row: Referee) => {
        const publication = row.publicationName || "";
        const edition = row.edition || "";

        if (!publication && !edition) return "N/A";
        if (!publication) return edition;
        if (!edition) return publication;
        return `${publication} - ${edition}`;
      },
    },
    {
      header: "Region",
      accessor: "regionName",
      render: (_value: unknown, row: RefereeWithTranslations) => {
        const original = row.regionName || "N/A";
        const translated = row.regionTranslated;

        if (!translated || translated === original) {
          return original;
        }

        return (
          <div className={refereeStyles.translationContainer}>
            <div>{original}</div>
            <div className={refereeStyles.translatedText}>{translated}</div>
          </div>
        );
      },
    },
    {
      header: "Practice",
      accessor: "practiceGroupName",
      render: (_value: unknown, row: RefereeWithTranslations) => {
        const original = getSubmissionPracticeFullName(row as unknown as Submission);
        const translated = getSubmissionPracticeFullNameTranslated(
          row as unknown as SubmissionWithTranslations,
        );

        if (!translated || translated === original || translated === "-") {
          return original;
        }

        return (
          <div className={refereeStyles.translationContainer}>
            <div>{original}</div>
            <div className={refereeStyles.translatedText}>{translated}</div>
          </div>
        );
      },
    },
    {
      header: "Title",
      accessor: "title",
      render: (value: unknown) => (value === null || value === undefined ? "—" : String(value)),
    },
    {
      header: "Name",
      accessor: "firstName",
      render: (_value: unknown, row: Referee) => `${row.firstName} ${row.surname}`,
    },
    {
      header: "Company Name",
      accessor: "companyName",
      render: (value: unknown) => (value === null || value === undefined ? "—" : String(value)),
    },
    {
      header: "Email",
      accessor: "emailAddress",
      render: (value: unknown) => (value === null || value === undefined ? "N/A" : String(value)),
    },
    {
      header: "Referee Category",
      accessor: "refereeCategory",
      render: (value: unknown) => (value === null || value === undefined ? "—" : String(value)),
    },
    {
      header: "Barrister",
      accessor: "barristerName",
      render: (value: unknown) => (value === null || value === undefined ? "—" : String(value)),
    },
    {
      header: "Created At",
      accessor: "createdAt",
      render: (value: unknown) => {
        if (!value) return "-";
        try {
          return formatDateShort(String(value));
        } catch (error) {
          console.error("Failed to format date:", error);
          return "-";
        }
      },
    },
    {
      header: "Post Date",
      accessor: "refereeCreatedAt",
      render: (value: unknown) => {
        if (!value) return "-";
        try {
          return formatDateShort(String(value));
        } catch (error) {
          console.error("Failed to format date:", error);
          return "-";
        }
      },
    },
    {
      header: "Username",
      accessor: "refereeCreatedByEmail",
      render: (value: unknown) => (value === null || value === undefined ? "—" : String(value)),
    },
    {
      header: "Language Code",
      accessor: "languageCode",
      render: (value: unknown) => (value === null || value === undefined ? "-" : String(value)),
    },
    {
      header: "Subtype",
      accessor: "subtype",
      render: (value: unknown) => String(value || "-"),
    },
    {
      header: "Code",
      accessor: "code",
      render: (value: unknown) => String(value || "-"),
    },
    {
      header: "Response Date",
      accessor: "responseDate",
      render: (value: unknown) => {
        if (!value) return "-";
        try {
          return formatDateShort(String(value));
        } catch (error) {
          console.error("Failed to format date:", error);
          return "-";
        }
      },
    },
    {
      header: "Survey Answered",
      accessor: "surveyAnswered",
      render: (value: unknown) => {
        if (value === null || value === undefined) return "—";
        return value === true ? "✔" : "";
      },
    },
  ];

  const editionOptions = useMemo(() => {
    const years: { value: string; label: string }[] = [];
    for (let year = currentYear + 1; year >= 2017; year--) {
      years.push({ value: String(year), label: String(year) });
    }
    return years;
  }, [currentYear]);

  const regionOptions = useMemo(() => {
    return regionOptionsState;
  }, [regionOptionsState]);

  const publicationOptions = useMemo(() => {
    const base = [{ value: "all", label: "All publications" }];
    if (mode === "new") {
      return [
        ...base,
        ...(publicationUniques?.values ?? [])
          .filter(v => taxonomyOptionFitsMode("publication", v, mode))
          .map(v => ({
            value: taxonomyOptionValue("publication", v, mode),
            label: publicationOptionLabel(v),
          })),
      ];
    }
    const pubs = publicationsData?.publications ?? [];
    return [
      ...base,
      ...pubs.map((p: LegacyPublication) => ({
        value: String(p.id),
        label: p.publication
          ? `${p.publication} (${p.edition ?? "n/a"})`
          : `Publication ${p.id}${p.edition ? ` (${p.edition})` : ""}`,
      })),
    ];
  }, [publicationsData?.publications, publicationUniques?.values, mode]);

  const surveyAnsweredOptions = useMemo(
    () => [
      { value: "all", label: "All" },
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
    [],
  );

  const FILTER_CONFIGS = [
    {
      key: "edition",
      label: "Edition",
      options: editionOptions,
    },
    {
      key: "publication",
      label: "Publication",
      options: publicationOptions,
    },
    {
      key: "region",
      label: "Region",
      options: regionOptions,
    },
    {
      key: "surveyAnswered",
      label: "Survey Answered",
      options: surveyAnsweredOptions,
    },
  ];

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    updateUrl({ search: value, page: "1" });
  };

  const handleFilterChangeWithDeps = (key: string, value: string | number | boolean) => {
    setCurrentPage(1);

    if (key === "edition") {
      handleFilterChange("edition", value);
      handleFilterChange("publication", "all");
      handleFilterChange("region", "all");
      return;
    }

    if (key === "publication") {
      handleFilterChange("publication", value);
      handleFilterChange("region", "all");
      return;
    }

    if (key === "region") {
      if (filters.publication === "all") {
        handleFilterChange("region", "all");
      } else {
        handleFilterChange(key, value);
      }
      return;
    }

    if (key === "surveyAnswered") {
      handleFilterChange(key, value);
      return;
    }

    handleFilterChange(key, value);
  };

  const handleClearFilterWithDeps = (key: string) => {
    setCurrentPage(1);

    if (key === "edition") {
      handleFilterChange("edition", String(currentYear));
      handleFilterChange("publication", "all");
      handleFilterChange("region", "all");
      return;
    }

    if (key === "publication") {
      handleFilterChange("publication", "all");
      handleFilterChange("region", "all");
      return;
    }

    if (key === "region") {
      handleFilterChange("region", "all");
      return;
    }

    if (key === "surveyAnswered") {
      handleFilterChange("surveyAnswered", "all");
      return;
    }

    handleClearFilter?.(key);
  };

  const handleTaxonomyChange = (next: TaxonomyMode) => {
    setFilters(prev => ({ ...prev, publication: "all", region: "all" }));
    setCurrentPage(1);
    updateUrl({ [TAXONOMY_URL_PARAM]: next, publication: "all", region: "all", page: "1" });
  };

  const handleExport = async () => {
    setExportError(null);
    setIsExporting(true);
    try {
      await refereesService.exportCsv({
        publicationId,
        regionId,
        editionId,
        rankingEntityId,
        taxonomy,
        edition: filters.edition,
        search: searchTerm || undefined,
        surveyAnswered:
          filters.surveyAnswered === "all" ? undefined : (filters.surveyAnswered as "yes" | "no"),
      });
    } catch (error) {
      setExportError(error instanceof Error ? error.message : "Failed to export referees");
    } finally {
      setIsExporting(false);
    }
  };

  const totalPages = Math.ceil(pagination.total / ITEMS_PER_PAGE);
  const startIndex = pagination.offset;
  const endIndex = Math.min(startIndex + referees.length, pagination.total);

  const enhancedActiveFilters = useMemo(() => {
    return activeFilters.map(filter => {
      if (filter.key === "publication") {
        const match = publicationOptions.find(opt => String(opt.value) === filters.publication);
        return {
          ...filter,
          displayValue: match?.label ?? String(filter.value),
        } as const;
      }
      if (filter.key === "region") {
        const match = regionOptions.find(opt => String(opt.value) === filters.region);
        return {
          ...filter,
          displayValue: match?.label ?? String(filter.value),
        } as const;
      }
      if (filter.key === "surveyAnswered") {
        const match = surveyAnsweredOptions.find(
          opt => String(opt.value) === filters.surveyAnswered,
        );
        return {
          ...filter,
          displayValue: match?.label ?? String(filter.value),
        } as const;
      }
      return { ...filter, displayValue: filter.displayValue ?? String(filter.value) } as const;
    });
  }, [
    activeFilters,
    filters.publication,
    filters.region,
    filters.surveyAnswered,
    publicationOptions,
    regionOptions,
    surveyAnsweredOptions,
  ]);

  if (error) {
    const status = (error as Error & { status?: number }).status;
    const isAuthError = status === 401 || status === 403;
    if (isAuthError) {
      return <UnauthorizedView />;
    }
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader title="Referee Contact List">
          <div className={styles.headerActions}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleExport}
              disabled={isExporting || pagination.total === 0}
            >
              {isExporting ? "Exporting..." : "Export referees"}
            </Button>
          </div>
        </PageHeader>
      </div>

      <div className={styles.searchSection}>
        <SearchBar
          value={searchTerm}
          onChange={handleSearchChange}
          onClear={() => handleSearchChange("")}
          placeholder="Search by practice, region, firm, name, email, company..."
        />
      </div>

      {toggleVisible && <TaxonomyToggle mode={mode} onChange={handleTaxonomyChange} />}

      <DataFilters
        filters={FILTER_CONFIGS}
        values={filters}
        onChange={handleFilterChangeWithDeps}
        activeFilters={enhancedActiveFilters}
        onClearFilter={handleClearFilterWithDeps}
      >
        <div className={styles.rowsPerPageControl}>
          <label htmlFor="itemsPerPage" className={styles.rowsPerPageLabel}>
            Rows per page:
          </label>
          <span>{ITEMS_PER_PAGE}</span>
        </div>
      </DataFilters>

      {isLoading && <LoadingState message="Loading referees..." />}

      {error && !isLoading && (
        <EmptyState
          variant="error"
          title="Failed to load referee contacts"
          message={error.message}
          action={{
            label: "Retry",
            onClick: () => window.location.reload(),
          }}
        />
      )}

      {translationsError && !isLoading && (
        <EmptyState
          variant="info"
          title="Translations failed to load"
          message={translationsError.message}
        />
      )}

      {exportError && !isExporting && (
        <EmptyState variant="error" title="Export failed" message={exportError} />
      )}

      {!isLoading && !error && (
        <>
          <DataTable data={displayReferees} columns={columns} size="small" rowKey="id" />

          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            startIndex={startIndex + 1}
            endIndex={endIndex}
            totalCount={pagination.total}
            itemName="referees"
            onPageChange={page => {
              setCurrentPage(page);
              updateUrl({ page: String(page) });
            }}
          />
        </>
      )}
    </div>
  );
}

export default RefereeContactList;
